import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Notification from "./Notification";
import axios from "axios";
import { FiUser } from "react-icons/fi";
import { FaUsers, FaLayerGroup, FaSync } from "react-icons/fa";


const ProjectManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true));
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [showAssignGroupModal, setShowAssignGroupModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const user = JSON.parse(localStorage.getItem("user"));
  const company_id = user ? user.company_id : "";
  const billing_account_id = user ? user.billing_account_id : "";

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects/company/${company_id}`);
      setProjects(res.data);
    } catch (error) {
      setNotification({ type: "error", message: "Failed to fetch projects." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${billing_account_id}`);
      setNotification({ type: "success", message: "Project sync successful!" });
      fetchProjects();
    } catch (error) {
      setNotification({ type: "error", message: "Failed to sync projects." });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/company/${company_id}`);
      setUsers(res.data);
    } catch {}
  };
  const fetchGroups = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/groups/company/${company_id}`);
      setGroups(res.data);
    } catch {}
  };

  const openAssignUserModal = (project) => {
    setSelectedProject(project);
    setShowAssignUserModal(true);
    fetchUsers();
  };
  const openAssignGroupModal = (project) => {
    setSelectedProject(project);
    setShowAssignGroupModal(true);
    fetchGroups();
  };
  const handleAssignUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/project`, {
        id_user: selectedUser,
        project_id: selectedProject.project_id,
      });
      setNotification({ type: "success", message: "User assigned to project!" });
      setShowAssignUserModal(false);
    } catch {
      setNotification({ type: "error", message: "Failed to assign user." });
    }
  };
  const handleAssignGroup = async (e) => {
    e.preventDefault();
    if (!selectedGroup) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/groups/${selectedGroup}/projects`, [selectedProject.project_id], {
        headers: { "Content-Type": "application/json" },
      });
      setNotification({ type: "success", message: "Project assigned to group!" });
      setShowAssignGroupModal(false);
    } catch {
      setNotification({ type: "error", message: "Failed to assign group." });
    }
  };

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line
  }, []);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const closeNotification = () => setNotification(null);

  return (
    <div className="flex flex-col min-h-screen">
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
      <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} ml-0`}>
          <div className="pt-20 pb-6">
            <div className="mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 px-4 sm:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-2 rounded-lg mr-3">
                      <FaLayerGroup className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Project Management</h2>
                      <p className="text-sm text-gray-600">View and manage your company projects</p>
                    </div>
                  </div>
                  <button
                    onClick={handleSync}
                    className="flex items-center bg-blue-600 text-white py-2 px-4 rounded-lg shadow-sm hover:bg-blue-700 transition-all duration-200 hover:shadow-md"
                    disabled={isLoading}
                  >
                    <FaSync className="w-4 h-4 mr-2" />
                    Sync Project
                  </button>
                </div>
                <div className="overflow-x-auto">
                  {isLoading ? (
                    <div className="bg-white rounded-xl p-6 mb-8">
                      {/* Skeleton Table Header */}
                      <div className="grid grid-cols-4 gap-4 mb-4">
                        <div className="h-4 bg-gray-300 rounded animate-pulse w-24"></div>
                        <div className="h-4 bg-gray-300 rounded animate-pulse w-32"></div>
                        <div className="h-4 bg-gray-300 rounded animate-pulse w-28"></div>
                        <div className="h-4 bg-gray-300 rounded animate-pulse w-20"></div>
                      </div>

                      {/* Skeleton Table Rows */}
                      {[...Array(5)].map((_, index) => (
                        <div key={index} className="grid grid-cols-4 gap-4 mb-3 border-b border-gray-100 pb-2">
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="overflow-x-auto w-full">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing Account ID</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {projects.map((project) => (
                            <tr key={project.project_id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{project.project_id}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{project.company_id}</td>
                              <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-600">{project.billing_account_id}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-right">
                                <div className="inline-flex space-x-2">
                                  <div className="relative group">
                                    <button
                                      className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 focus:outline-none"
                                      onClick={() => openAssignUserModal(project)}
                                      aria-label="Assign to User"
                                      type="button"
                                    >
                                      <FiUser className="w-4 h-4" />
                                    </button>
                                    <span className="absolute left-1/2 -translate-x-1/2 -top-8 z-10 px-2 py-1 text-xs rounded bg-gray-900 text-white opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap shadow-lg">
                                      Assign to User
                                    </span>
                                  </div>
                                  <div className="relative group">
                                    <button
                                      className="bg-green-500 text-white p-2 rounded hover:bg-green-600 focus:outline-none"
                                      onClick={() => openAssignGroupModal(project)}
                                      aria-label="Assign to Group"
                                      type="button"
                                    >
                                      <FaUsers className="w-4 h-4" />
                                    </button>
                                    <span className="absolute left-1/2 -translate-x-1/2 -top-8 z-10 px-2 py-1 text-xs rounded bg-gray-900 text-white opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap shadow-lg">
                                      Assign to Group
                                    </span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {notification && (<Notification type={notification.type} message={notification.message} onClose={closeNotification} />)}
      {/* Modal Assign User */}
      {showAssignUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Assign Project to User</h3>
              <button onClick={() => setShowAssignUserModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">×</button>
            </div>
            <form onSubmit={handleAssignUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select User</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">Select a user</option>
                  {users.map((u) => (
                    <option key={u.id_user} value={u.id_user}>{u.full_name} ({u.role})</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => setShowAssignUserModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Assign</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal Assign Group */}
      {showAssignGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Assign Project to Group</h3>
              <button onClick={() => setShowAssignGroupModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">×</button>
            </div>
            <form onSubmit={handleAssignGroup} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Group</label>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">Select a group</option>
                  {groups.map((g) => (
                    <option key={g.group_id} value={g.group_id}>{g.group_name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => setShowAssignGroupModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Assign</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectManagement;
